'use client';

import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Tour, TourStop } from '@/types';

interface TourTranslation {
  title: string;
  description: string;
  city: string;
  country: string;
  stops: Array<{ stopId: string; title: string; description: string }>;
}

/**
 * Apply translation to a tour object (mutates in place for performance)
 */
export async function applyTourTranslation(
  tour: Tour,
  locale: string
): Promise<void> {
  const lang = locale.split('-')[0];
  if (!lang || lang === 'es') return; // Spanish is the original language

  try {
    const transDoc = await getDoc(doc(db, 'tours', tour.id, 'translations', lang));
    if (transDoc.exists()) {
      const trans = transDoc.data() as TourTranslation;
      if (trans.title) tour.title = trans.title;
      if (trans.description) tour.description = trans.description;
      if (trans.city) tour.city = trans.city;
      if (trans.country) tour.country = trans.country;
    }
  } catch (e) {
    // Silently fail - show original language
  }
}

/**
 * Apply translation to tour stops (mutates in place)
 */
export async function applyStopTranslations(
  tourId: string,
  stops: TourStop[],
  locale: string
): Promise<void> {
  const lang = locale.split('-')[0];
  if (!lang || lang === 'es') return;

  try {
    const transDoc = await getDoc(doc(db, 'tours', tourId, 'translations', lang));
    if (transDoc.exists()) {
      const trans = transDoc.data() as TourTranslation;
      if (trans.stops) {
        for (const ts of trans.stops) {
          const stop = stops.find(s => s.id === ts.stopId);
          if (stop) {
            if (ts.title) stop.title = ts.title;
            if (ts.description) stop.description = ts.description;
          }
        }
      }
    }
  } catch (e) {
    // Silently fail
  }
}

/**
 * Translate an array of tours (for listings)
 * Uses Promise.allSettled for performance
 */
export async function translateTours(
  tours: Tour[],
  locale: string
): Promise<Tour[]> {
  const lang = locale.split('-')[0];
  if (!lang || lang === 'es') return tours;

  await Promise.allSettled(
    tours.map(tour => applyTourTranslation(tour, locale))
  );

  return tours;
}
